import { api } from './client';

export interface OutwardGrnItem {
  id: number;
  itemCode: string | null;
  itemName: string;
  unit: string;
  doQty: string;
  packedQty: string;
  condition: string;
}

export interface OutwardGrn {
  id: number;
  grnNo: string;
  grnDate: string;
  doId: number;
  packingId: number;
  partyId: number;
  transporterName: string | null;
  vehicleNo: string | null;
  driverName: string | null;
  lrNo: string | null;
  ewayBillNo: string | null;
  boxCount: number;
  grossWeightKg: string;
  gateOfficer: string | null;
  isPartial: boolean;
  partialNote: string | null;
  status: string;
  // Dispatch weight check
  weighbridgeGrossKg?: string | null;
  expectedNetKg?: string | null;
  actualNetKg?: string | null;
  variancePct?: string | null;
  holdReason?: string | null;
  overrideBy?: string | null;
  overrideAt?: string | null;
  overrideNote?: string | null;
  /** Dispatch photos — JSON array of upload URLs (see outwardGrnPhotos). */
  photoUrls?: string | null;
  items: OutwardGrnItem[];
  party?: { id: number; name: string };
  dispatchOrder?: { id: number; doNo: string; enquiry?: { enqNo: string; source?: string | null; remarks?: string | null } };
  packing?: { id: number; packNo: string; status: string };
  salesInvoices?: Array<{ id: number; invoiceNo: string; status: string }>;
  onlineCustomer?: string | null;
}

export async function listOutwardGrns(filter: {
  status?: string;
  from?: string;
  to?: string;
  search?: string;
  channel?: string;
} = {}): Promise<OutwardGrn[]> {
  const r = await api.get<OutwardGrn[]>('/outward-grn', { params: filter });
  return r.data;
}

export function outwardGrnExportPath(filter: { status?: string; from?: string; to?: string; search?: string } = {}): string {
  const search = new URLSearchParams(
    Object.fromEntries(Object.entries(filter).filter(([, v]) => v != null && v !== '')) as Record<string, string>,
  ).toString();
  return `/outward-grn/export.xlsx${search ? `?${search}` : ''}`;
}

export async function getOutwardGrn(id: number): Promise<OutwardGrn> {
  const r = await api.get<OutwardGrn>(`/outward-grn/${id}`);
  return r.data;
}

export interface LastTransport { grnNo: string; grnDate: string; transporterName: string | null; vehicleNo: string | null; driverName: string | null }

/** Transport details from the selected packing's customer's last dispatch (null if none). */
export async function getLastTransport(packingId: number): Promise<LastTransport | null> {
  const r = await api.get<LastTransport | ''>(`/outward-grn/last-transport/${packingId}`);
  return r.data || null;
}

export async function createOutwardGrn(payload: {
  packingId: number;
  grnDate?: string;
  transporterName?: string;
  vehicleNo?: string;
  driverName?: string;
  lrNo?: string;
  ewayBillNo?: string;
  gateOfficer?: string;
  isPartial?: boolean;
  partialNote?: string;
  holdReason?: string;
}): Promise<OutwardGrn> {
  const r = await api.post<OutwardGrn>('/outward-grn', payload);
  return r.data;
}

/** Outward GRNs held by the weight check — the override queue. */
export async function listGrnHolds(): Promise<OutwardGrn[]> {
  const r = await api.get<OutwardGrn[]>('/outward-grn/holds');
  return r.data;
}

/** Director / Admin overrides a held GRN — issues the goods. */
export async function overrideGrnHold(id: number, note?: string): Promise<OutwardGrn> {
  const r = await api.post<OutwardGrn>(`/outward-grn/${id}/override`, { note });
  return r.data;
}

/** Director / Admin cancels a held GRN. */
export async function cancelGrnHold(id: number, reason: string): Promise<OutwardGrn> {
  const r = await api.post<OutwardGrn>(`/outward-grn/${id}/cancel`, { reason });
  return r.data;
}

/** Dispatch photos already on an Outward GRN (stored as a JSON array of URLs) — shown, no longer uploaded. */
export function outwardGrnPhotos(g: { photoUrls?: string | null }): string[] {
  if (!g.photoUrls) return [];
  try { const a = JSON.parse(g.photoUrls); return Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string') : []; } catch { return []; }
}
